import { memo } from "react";

import { ResponsiveLine, Serie } from "@nivo/line";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

// -----------------------------------------------------------------------------

export const OPEN_LINE_CHART_ID = "risk-linechart-open-findings-expanded";
export const CLOSE_LINE_CHART_ID = "risk-linechart-close-findings-expanded";

// -----------------------------------------------------------------------------

const LineChartModal = memo(function LineChartModal({
  data,
  title,
  onClose,
  isOpen,
}: OrganizationFindingModalProps): JSX.Element | null {
  const hasAtLeastOneDatapoint = data.some(
    (category: Serie) => category.data.length > 0
  );

  //
  return hasAtLeastOneDatapoint ? (
    <Modal
      open={isOpen as any}
      onClose={onClose as ModalOnCloseCallback}
      sx={{ display: "flex", overflowY: "auto", padding: 16 }}
    >
      <Paper
        sx={{
          padding: "16px",
          maxWidth: "90vw",
          height: "475px",
          width: "100%",
          "@media screen and (max-width:640px)": {
            borderRadius: 0,
            height: "100%",
            overflowY: "auto",
            width: "100%",

            "& .MuiBox-root": {
              flexDirection: "column",
            },
          },
        }}
      >
        <Typography variant="overline">{title}</Typography>
        <Box
          sx={{ height: 400 }}
          id={isOpen ? OPEN_LINE_CHART_ID : CLOSE_LINE_CHART_ID}
        >
          <ResponsiveLine
            data={data}
            enablePoints={true}
            areaBlendMode="multiply"
            areaOpacity={0.9}
            pointSize={4}
            colors={isOpen ? ["#FF6257"] : ["#3C9D40"]}
            margin={{ top: 2, right: 40, bottom: 35, left: 40 }}
            yScale={{ type: "linear", min: "auto", max: "auto" }}
            axisBottom={{
              tickValues: 5,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            axisLeft={{
              tickValues: 2,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            useMesh={true}
            defs={[
              {
                id: "gradientOpen",
                type: "linearGradient",
                colors: [
                  { offset: 0, color: "hsla(4, 90%, 47%, 0.1)" },
                  { offset: 100, color: "hsla(4, 90%, 47%, 0)" },
                ],
              },
              {
                id: "gradientResolved",
                type: "linearGradient",
                colors: [
                  { offset: 0, color: "hsla(122, 45%, 43%, 0.1)" },
                  { offset: 100, color: "hsla(0, 0%, 100%, 0)" },
                ],
              },
            ]}
            fill={[
              { match: "*", id: isOpen ? "gradientOpen" : "gradientResolved" },
            ]}
            tooltip={({ point }) => (
              <div
                style={{
                  color: "#fff",
                  background: "#333",
                  padding: "12px 16px",
                }}
              >
                {`Date: ${point.data.xFormatted}`}
                <br />
                {`Findings: ${point.data.yFormatted}`}
              </div>
            )}
          />
        </Box>
      </Paper>
    </Modal>
  ) : null;
});

export default LineChartModal;

// -----------------------------------------------------------------------------

interface OrganizationFindingModalProps {
  data: Serie[];
  title: JSX.Element;
  onClose: Function;
  isOpen?: boolean;
}

type ModalOnCloseCallback = (
  event: {},
  reason: "backdropClick" | "escapeKeyDown"
) => void;
